import { useState, useMemo } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Eye, EyeOff, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("token") || "").trim();
  }, [location.search]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (user) return <Navigate to="/portfolio" replace />;

  const validate = () => {
    if (password.length < 6) return "Password must be at least 6 characters.";
    if (password !== confirm) return "Passwords do not match.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setSuccess(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#050505]" data-testid="reset-password-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/15693274/pexels-photo-15693274.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Dubai skyline"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="font-serif text-3xl text-white mb-3">A new password,<br />a fresh start.</h2>
          <p className="text-white/65 text-sm font-sans">
            For your security, this link can only be used once. After resetting, you'll be signed out everywhere else.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-10">
            <img src="/equia-logo.png" alt="EQUIA Global Property" className="h-20 w-auto" />
          </div>

          {!token ? (
            <div data-testid="reset-missing-token" className="border border-red-500/20 bg-red-500/5 px-6 py-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-serif text-lg text-white mb-1">Missing reset token</h3>
                  <p className="text-white/85 text-sm leading-relaxed font-sans">
                    The reset link is incomplete. Please request a new password reset email.
                  </p>
                </div>
              </div>
            </div>
          ) : success ? (
            <div data-testid="reset-success" className="border border-[#D4AF37]/20 bg-[#0A0A0A] px-6 py-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-serif text-lg text-white mb-1">Password updated</h3>
                  <p className="text-white/85 text-sm leading-relaxed font-sans">
                    Redirecting you to sign in...
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <h2 className="font-serif text-xl text-white mb-1">Set a new password</h2>
              <p className="text-white/60 text-sm mb-8 font-sans">
                Choose a strong password. Minimum 6 characters.
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm mb-6" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-[11px] tracking-[0.15em] uppercase text-white/65 font-sans mb-2 block">New Password</label>
                  <div className="relative">
                    <input
                      data-testid="reset-password-input"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none pr-12"
                      placeholder="Min. 6 characters"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/65 hover:text-white/85 transition-colors"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] tracking-[0.15em] uppercase text-white/65 font-sans mb-2 block">Confirm Password</label>
                  <input
                    data-testid="reset-confirm-input"
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none"
                    placeholder="Repeat password"
                    required
                    minLength={6}
                  />
                </div>

                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={loading || !password || !confirm}
                  className="w-full bg-[#D4AF37] text-black font-medium py-3 text-sm tracking-wide hover:bg-[#B5952F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </form>
            </>
          )}

          <div className="mt-8">
            <Link
              to="/login"
              data-testid="reset-back-to-login"
              className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors font-sans inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
